import React from 'react';

const ChurchLogo = ({ variant = 'yellow-blue', size = 'default' }) => {
  const sizes = {
    small: { width: 48, height: 56 },
    default: { width: 64, height: 74 },
    large: { width: 96, height: 112 }
  };

  const currentSize = sizes[size];

  // Color variants
  const variants = {
    'yellow-blue': {
      flame: { from: '#FFF9E6', via: '#FFD700', to: '#FFA500' },
      cross: '#FFFFFF',
      dove: '#4FC3F7',
      bible: { main: '#2196F3', light: '#64B5F6', dark: '#1976D2' }
    },
    'gold-royal': {
      flame: { from: '#FFFACD', via: '#FFD700', to: '#DAA520' },
      cross: '#FFFFFF',
      dove: '#B8860B',
      bible: { main: '#4A148C', light: '#7B1FA2', dark: '#311B92' }
    },
    'fire-red': {
      flame: { from: '#FFF5E6', via: '#FF8C00', to: '#FF4500' },
      cross: '#FFFFFF',
      dove: '#FFD700',
      bible: { main: '#8B0000', light: '#DC143C', dark: '#660000' }
    },
    'emerald-gold': {
      flame: { from: '#FFFACD', via: '#FFD700', to: '#F59E0B' },
      cross: '#FFFFFF',
      dove: '#10B981',
      bible: { main: '#047857', light: '#34D399', dark: '#065F46' }
    },
    'purple-gold': {
      flame: { from: '#FFF9E6', via: '#FFD700', to: '#F59E0B' },
      cross: '#FFFFFF',
      dove: '#A78BFA',
      bible: { main: '#6D28D9', light: '#8B5CF6', dark: '#5B21B6' }
    },
    'ocean-sunrise': {
      flame: { from: '#FFE4B5', via: '#FF7F50', to: '#FF6347' },
      cross: '#FFFFFF',
      dove: '#00CED1',
      bible: { main: '#1E90FF', light: '#87CEEB', dark: '#00008B' }
    },
    'royal-crimson': {
      flame: { from: '#FFD700', via: '#FFA500', to: '#FF8C00' },
      cross: '#FFFFFF',
      dove: '#DC143C',
      bible: { main: '#800020', light: '#CD5C5C', dark: '#8B0000' }
    },
    'forest-flame': {
      flame: { from: '#FFFFE0', via: '#FFD700', to: '#FF8C00' },
      cross: '#FFFFFF',
      dove: '#228B22',
      bible: { main: '#006400', light: '#32CD32', dark: '#013220' }
    },
    'midnight-gold': {
      flame: { from: '#FFE4B5', via: '#DAA520', to: '#B8860B' },
      cross: '#FFFFFF',
      dove: '#4169E1',
      bible: { main: '#191970', light: '#4682B4', dark: '#000080' }
    },
    'sunrise-glory': {
      flame: { from: '#FFFACD', via: '#FFA500', to: '#FF4500' },
      cross: '#FFFFFF',
      dove: '#FF69B4',
      bible: { main: '#FF1493', light: '#FFB6C1', dark: '#C71585' }
    }
  };

  const colors = variants[variant];

  return (
    <svg
      width={currentSize.width}
      height={currentSize.height}
      viewBox="0 0 120 140"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className="transition-all duration-300 hover:scale-105"
    >
      <defs>
        {/* Flame Gradient */}
        <radialGradient id={`flameGrad-${variant}`} cx="50%" cy="40%">
          <stop offset="0%" stopColor={colors.flame.from} />
          <stop offset="50%" stopColor={colors.flame.via} />
          <stop offset="100%" stopColor={colors.flame.to} />
        </radialGradient>
        
        {/* Bible Gradient */}
        <linearGradient id={`bibleGrad-${variant}`} x1="0%" y1="0%" x2="0%" y2="100%">
          <stop offset="0%" stopColor={colors.bible.light} />
          <stop offset="50%" stopColor={colors.bible.main} />
          <stop offset="100%" stopColor={colors.bible.dark} />
        </linearGradient>
      </defs>

      {/* Flames Background */}
      <path
        d="M60 10 L70 35 Q75 30 78 40 L82 25 Q85 20 88 30 L90 18 Q88 45 80 55 L75 65 Q60 75 60 75 Q60 75 45 65 L40 55 Q32 45 30 18 L32 30 Q35 20 38 25 L42 40 Q45 30 50 35 Z"
        fill={`url(#flameGrad-${variant})`}
        opacity="0.95"
      />

      {/* Cross - White */}
      <rect x="55" y="28" width="10" height="35" rx="2" fill={colors.cross} />
      <rect x="45" y="40" width="30" height="10" rx="2" fill={colors.cross} />

      {/* Dove */}
      <path
        d="M70 55 Q75 52 80 55 L82 58 Q83 60 81 62 L78 65 Q75 67 72 66 L68 64 Q65 62 63 58 L62 55 Q63 52 66 51 L70 50 Z M82 58 L88 62 Q90 64 89 66 L86 68 Q84 69 82 67 L80 64 Z M82 57 L87 55 Q89 53 91 54 L93 56 Q94 58 92 60 L88 62 Z"
        fill={colors.dove}
        opacity="0.9"
      />

      {/* Open Bible */}
      <g transform="translate(30, 75)">
        {/* Left page */}
        <path
          d="M5 5 L25 5 L30 35 L5 35 Q3 30 2 20 Q2 10 5 5 Z"
          fill={`url(#bibleGrad-${variant})`}
          opacity="0.8"
        />
        {/* Right page */}
        <path
          d="M35 5 L55 5 Q58 10 58 20 Q58 30 55 35 L30 35 L35 5 Z"
          fill={`url(#bibleGrad-${variant})`}
          opacity="0.8"
        />
        {/* Center spine */}
        <rect x="28" y="5" width="4" height="30" fill={colors.bible.dark} />
        
        {/* Page lines */}
        <line x1="8" y1="12" x2="22" y2="12" stroke={colors.bible.light} strokeWidth="0.5" opacity="0.6" />
        <line x1="8" y1="17" x2="22" y2="17" stroke={colors.bible.light} strokeWidth="0.5" opacity="0.6" />
        <line x1="8" y1="22" x2="22" y2="22" stroke={colors.bible.light} strokeWidth="0.5" opacity="0.6" />
        
        <line x1="38" y1="12" x2="52" y2="12" stroke={colors.bible.light} strokeWidth="0.5" opacity="0.6" />
        <line x1="38" y1="17" x2="52" y2="17" stroke={colors.bible.light} strokeWidth="0.5" opacity="0.6" />
        <line x1="38" y1="22" x2="52" y2="22" stroke={colors.bible.light} strokeWidth="0.5" opacity="0.6" />
      </g>
    </svg>
  );
};

export default ChurchLogo;